import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  MenuItem,
  Paper,
  Popover,
  Select,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";
import {
  AddRounded as AddRoundedIcon,
  AutoGraphRounded as AutoGraphRoundedIcon,
  CalculateRounded as CalculateRoundedIcon,
  DeleteOutlineRounded as DeleteOutlineRoundedIcon,
  FilterAltRounded as FilterAltRoundedIcon,
  FilterListOffRounded as FilterListOffRoundedIcon,
  GridViewRounded as GridViewRoundedIcon,
  ManageSearchRounded as ManageSearchRoundedIcon,
  RefreshRounded as RefreshRoundedIcon,
  SaveRounded as SaveRoundedIcon,
} from "@mui/icons-material";
import { settingsService } from "../../services/settings/settingsService";
import {
  DEFAULT_HOUSEHOLD_CHART_PROFILES,
  DEFAULT_HOUSEHOLD_TABLE_PROFILES,
  HouseholdChartCard,
  HouseholdChartMetric,
  HouseholdChartProfile,
  HOUSEHOLD_TASK_FREQUENCIES,
  HouseholdTaskDimensionLists,
  HouseholdTaskFilterableColumn,
  HouseholdTaskFiltersState,
  HouseholdTaskRow,
  HouseholdTableViewProfile,
  HouseholdTasksToolSettingsV3,
  createChartEntityId,
  createBlankHouseholdTaskRow,
  createTableViewEntityId,
  deriveDimensionLists,
  formatHours,
  normalizeChartProfile,
  normalizeHouseholdTaskRow,
  normalizeHouseholdTasksToolSettings,
  normalizeTableViewProfile,
  parseHouseholdTasksCsv,
  sortUnique,
  splitPeople,
} from "./householdTasksUtils";
import { HOUSEHOLD_TASKS_SEED_CSV } from "./householdTasksSeed";

type ToolTab = "tasks" | "charts" | "calculations";
type FilterableColumn = HouseholdTaskFilterableColumn;
type FiltersState = HouseholdTaskFiltersState;
type ManageListKey = keyof HouseholdTaskDimensionLists;

const CHART_METRIC_OPTIONS: Array<{
  value: HouseholdChartMetric;
  label: string;
}> = [
  {
    value: "responsibility",
    label: "Responsibility",
  },
  {
    value: "category",
    label: "Category",
  },
  {
    value: "type",
    label: "Type",
  },
  {
    value: "frequency",
    label: "Frequency",
  },
  {
    value: "capable",
    label: "Capable Group",
  },
];

const CHART_TYPE_OPTIONS = [
  { value: "pie", label: "Pie" },
  { value: "bar", label: "Bar" },
] as const;

const GOOGLE_CHART_COLORS = [
  "#4285F4",
  "#EA4335",
  "#FBBC05",
  "#34A853",
  "#46BDC6",
  "#7BAAF7",
  "#F28B82",
  "#FDD663",
  "#81C995",
  "#AECBFA",
  "#FCC934",
  "#A7FF83",
] as const;

const DEFAULT_WEDDING_VALUE = 1250;
const DEFAULT_LABOR_RATES = [35, 25, 15, 55, 45] as const;
const EXCLUDED_CALCULATION_CATEGORIES = new Set([
  "School",
  "Rest",
  "Business",
  "Sleep",
]);
const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const getChartDefaults = (
  metric: HouseholdChartMetric,
  chartType: HouseholdChartCard["chartType"]
) => {
  if (metric === "responsibility" && chartType === "pie") {
    return {
      title: "Time Per Person",
      color: "#4285F4",
    };
  }

  if (metric === "responsibility" && chartType === "bar") {
    return {
      title: "Time Per Person",
      color: "#4285F4",
    };
  }

  if (metric === "category") {
    return {
      title: "Time Per Category",
      color: "#EA4335",
    };
  }

  if (metric === "type") {
    return {
      title: "Time by Type",
      color: "#34A853",
    };
  }

  if (metric === "frequency") {
    return {
      title: "Time by Frequency",
      color: "#FBBC05",
    };
  }

  return {
    title: "Time Per Capable Group",
    color: "#7E57C2",
  };
};

const folderTabsSx = {
  minHeight: 42,
  "& .MuiTab-root": {
    minHeight: 42,
    borderRadius: "12px 12px 0 0",
    border: "1px solid",
    borderBottom: "none",
    borderColor: "divider",
    backgroundColor: "background.paper",
    textTransform: "none",
    fontWeight: 700,
    mr: 1,
    color: "text.secondary",
  },
  "& .Mui-selected": {
    color: "text.primary !important",
    background:
      "linear-gradient(180deg, rgba(93,196,178,0.20) 0%, rgba(93,196,178,0.08) 100%)",
  },
  "& .MuiTabs-indicator": {
    display: "none",
  },
} as const;

const tabBodySx = {
  border: "1px solid",
  borderColor: "divider",
  borderTop: "none",
  borderRadius: "0 14px 14px 14px",
  backgroundColor: "background.paper",
  overflow: "hidden",
} as const;

const FILTER_LABELS: Record<FilterableColumn, string> = {
  responsibility: "Responsibility",
  capable: "Capable",
  category: "Category",
  task: "Task",
  type: "Type",
  blockedBy: "Blocked By",
  frequency: "Frequency",
};

const getCellSx = (theme: Theme) => ({
  minWidth: 84,
  px: 0.35,
  py: 0.2,
  "& .MuiOutlinedInput-root": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? alpha(theme.palette.common.white, 0.02)
        : alpha(theme.palette.common.black, 0.01),
    alignItems: "flex-start",
  },
  "& .MuiOutlinedInput-input": {
    px: 0.75,
    py: 0.65,
    fontSize: "0.85rem",
    whiteSpace: "normal",
  },
  "& .MuiInputBase-inputMultiline": {
    px: "0 !important",
    py: "0 !important",
  },
});

const readOnlyCellSx = {
  px: 0.35,
  py: 0.2,
  verticalAlign: "top",
} as const;

const readOnlyContentSx = {
  py: 0.55,
  px: 0.7,
  borderRadius: 1,
  lineHeight: 1.35,
  whiteSpace: "normal",
  wordBreak: "break-word",
  overflowWrap: "anywhere",
  minHeight: 32,
} as const;

const sumTotalsBy = (
  rows: HouseholdTaskRow[],
  getKey: (row: HouseholdTaskRow) => string
) => {
  const totals = new Map<string, number>();
  rows.forEach((row) => {
    const key = getKey(row).trim() || "Unassigned";
    totals.set(key, (totals.get(key) || 0) + row.total);
  });
  return Array.from(totals.entries())
    .map(([label, total]) => ({ label, total }))
    .sort((left, right) => right.total - left.total);
};

const PIE_SLICE_LIMIT = 8;
const trimChartLabel = (label: string, maxLength = 18) =>
  label.length > maxLength ? `${label.slice(0, maxLength - 1)}…` : label;

const condensePieRows = (rows: Array<{ label: string; total: number }>) => {
  const normalizedRows = rows
    .filter((row) => row.total > 0)
    .map((row) => ({
      label: row.label || "Undecided",
      total: row.total,
    }));

  if (normalizedRows.length <= PIE_SLICE_LIMIT) {
    return normalizedRows;
  }

  const visibleRows = normalizedRows.slice(0, PIE_SLICE_LIMIT - 1);
  const remainingTotal = normalizedRows
    .slice(PIE_SLICE_LIMIT - 1)
    .reduce((sum, row) => sum + row.total, 0);

  return [
    ...visibleRows,
    {
      label: "Other",
      total: remainingTotal,
    },
  ];
};

const polarToCartesian = (
  centerX: number,
  centerY: number,
  radius: number,
  angleInDegrees: number
) => {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180;

  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
};

const describePieSlice = (
  centerX: number,
  centerY: number,
  radius: number,
  startAngle: number,
  endAngle: number
) => {
  const start = polarToCartesian(centerX, centerY, radius, endAngle);
  const end = polarToCartesian(centerX, centerY, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1;

  return [
    `M ${centerX} ${centerY}`,
    `L ${start.x} ${start.y}`,
    `A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`,
    "Z",
  ].join(" ");
};

const distributeLabelPositions = (
  items: Array<{
    id: string;
    x: number;
    y: number;
    anchor: "start" | "end";
    label: string;
    percent: number;
    color: string;
  }>
) => {
  const bySide = {
    start: items
      .filter((item) => item.anchor === "start")
      .sort((left, right) => left.y - right.y),
    end: items
      .filter((item) => item.anchor === "end")
      .sort((left, right) => left.y - right.y),
  };

  (["start", "end"] as const).forEach((anchor) => {
    const itemsOnSide = bySide[anchor];
    if (itemsOnSide.length === 0) return;

    const minY = 36;
    const maxY = 286;
    const slotCount = Math.max(1, itemsOnSide.length - 1);
    const spacing = slotCount === 0 ? 0 : (maxY - minY) / slotCount;

    bySide[anchor] = itemsOnSide.map((item, index) => {
      if (itemsOnSide.length === 1) {
        return { ...item, y: Math.max(minY, Math.min(maxY, item.y)) };
      }

      return {
        ...item,
        y: minY + spacing * index,
      };
    });
  });

  return [...bySide.start, ...bySide.end];
};

const GooglePieChart: React.FC<{
  title: string;
  rows: Array<{ label: string; total: number }>;
}> = ({ title, rows }) => {
  const [hoveredSliceId, setHoveredSliceId] = useState<string | null>(null);
  const chartRows = condensePieRows(rows);
  const total = chartRows.reduce((sum, row) => sum + row.total, 0);
  const centerX = 230;
  const centerY = 184;
  const radius = 102;
  let runningAngle = 0;
  const slices = chartRows.map((row, index) => {
    const percentage = total > 0 ? row.total / total : 0;
    const startAngle = runningAngle;
    const endAngle = startAngle + percentage * 360;
    runningAngle = endAngle;

    const midAngle = startAngle + (endAngle - startAngle) / 2;
    const lineStart = polarToCartesian(centerX, centerY, radius, midAngle);
    const lineTurn = polarToCartesian(centerX, centerY, radius + 18, midAngle);
    const anchor: "start" | "end" =
      Math.cos(((midAngle - 90) * Math.PI) / 180) >= 0 ? "start" : "end";
    const labelX = anchor === "start" ? 408 : 88;
    const lineEndX = anchor === "start" ? 392 : 104;
    const label = trimChartLabel(row.label || "Undecided");

    return {
      id: `${title}-${label}-${index}`,
      rawLabel: row.label || "Undecided",
      total: row.total,
      percentage,
      color: GOOGLE_CHART_COLORS[index % GOOGLE_CHART_COLORS.length],
      path: describePieSlice(centerX, centerY, radius, startAngle, endAngle),
      lineStart,
      lineTurn,
      lineEndX,
      label,
      labelY: lineTurn.y,
      labelX,
      anchor,
    };
  });

  const labels = distributeLabelPositions(
    slices.map((slice) => ({
      id: slice.id,
      x: slice.labelX,
      y: slice.labelY,
      anchor: slice.anchor,
      label: slice.label,
      percent: slice.percentage * 100,
      color: slice.color,
    }))
  );

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1,
        border: "1px solid #DADCE0",
        backgroundColor: "#FFFFFF",
        color: "#202124",
        fontFamily: "Arial, sans-serif",
        overflow: "hidden",
      }}
    >
      <Typography
        sx={{
          fontSize: 20,
          lineHeight: 1.2,
          mb: 1,
          color: "#5F6368",
          fontFamily: "Arial, sans-serif",
        }}
      >
        {title}
      </Typography>
      <Box sx={{ overflowX: "auto" }}>
        <svg
          viewBox="0 0 460 336"
          style={{
            width: "100%",
            minWidth: 420,
            height: "auto",
            display: "block",
          }}
        >
          {slices.map((slice) => (
            <g key={slice.id}>
              <path
                d={slice.path}
                fill={slice.color}
                stroke="#FFFFFF"
                strokeWidth={hoveredSliceId === slice.id ? "3" : "1.5"}
                onMouseEnter={() => setHoveredSliceId(slice.id)}
                onMouseLeave={() => setHoveredSliceId(null)}
                style={{
                  cursor: "pointer",
                  opacity:
                    hoveredSliceId && hoveredSliceId !== slice.id ? 0.72 : 1,
                  filter:
                    hoveredSliceId === slice.id
                      ? "drop-shadow(0 4px 10px rgba(0,0,0,0.18))"
                      : "none",
                  transform:
                    hoveredSliceId === slice.id ? "scale(1.025)" : "scale(1)",
                  transformOrigin: `${centerX}px ${centerY}px`,
                  transition:
                    "opacity 140ms ease, transform 140ms ease, filter 140ms ease, stroke-width 140ms ease",
                }}
              />
              <title>{`${slice.rawLabel}: ${(slice.percentage * 100).toFixed(
                1
              )}% (${formatHours(slice.total)} hrs)`}</title>
              <path
                d={`M ${slice.lineStart.x} ${slice.lineStart.y} L ${slice.lineTurn.x} ${slice.lineTurn.y} L ${slice.lineEndX} ${
                  labels.find((label) => label.id === slice.id)?.y ||
                  slice.labelY
                }`}
                fill="none"
                stroke={hoveredSliceId === slice.id ? "#5F6368" : "#9AA0A6"}
                strokeWidth={hoveredSliceId === slice.id ? "1.5" : "1"}
                style={{
                  opacity:
                    hoveredSliceId && hoveredSliceId !== slice.id ? 0.55 : 1,
                  transition: "opacity 140ms ease, stroke 140ms ease",
                }}
              />
            </g>
          ))}
          {labels.map((label) => (
            <g key={`${title}-${label.id}-text`}>
              <text
                x={label.x}
                y={label.y - 4}
                textAnchor={label.anchor}
                fontSize="11"
                fill={hoveredSliceId === label.id ? "#111827" : "#202124"}
                fontFamily="Arial, sans-serif"
                style={{
                  fontWeight: hoveredSliceId === label.id ? 700 : 400,
                  opacity:
                    hoveredSliceId && hoveredSliceId !== label.id ? 0.62 : 1,
                  transition: "opacity 140ms ease, fill 140ms ease",
                }}
              >
                {label.label}
              </text>
              <text
                x={label.x}
                y={label.y + 10}
                textAnchor={label.anchor}
                fontSize="10"
                fill={hoveredSliceId === label.id ? "#374151" : "#5F6368"}
                fontFamily="Arial, sans-serif"
                style={{
                  fontWeight: hoveredSliceId === label.id ? 700 : 400,
                  opacity:
                    hoveredSliceId && hoveredSliceId !== label.id ? 0.62 : 1,
                  transition: "opacity 140ms ease, fill 140ms ease",
                }}
              >
                {`${label.percent.toFixed(1).replace(/\.0$/, "")}%`}
              </text>
            </g>
          ))}
        </svg>
      </Box>
    </Box>
  );
};

const GoogleBarChart: React.FC<{
  title: string;
  rows: Array<{ label: string; total: number }>;
}> = ({ title, rows }) => {
  const [hoveredBarLabel, setHoveredBarLabel] = useState<string | null>(null);
  const chartRows = rows.filter((row) => row.total > 0).slice(0, 8);
  const max = Math.max(1, ...chartRows.map((row) => row.total));
  const chartHeight = 240;
  const baseY = 264;
  const barWidth = 26;
  const gap = 18;
  const startX = 46;

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1,
        border: "1px solid #DADCE0",
        backgroundColor: "#FFFFFF",
        color: "#202124",
        fontFamily: "Arial, sans-serif",
        overflow: "hidden",
      }}
    >
      <Typography
        sx={{
          fontSize: 20,
          lineHeight: 1.2,
          mb: 1,
          color: "#5F6368",
          fontFamily: "Arial, sans-serif",
        }}
      >
        {title}
      </Typography>
      <Box sx={{ overflowX: "auto" }}>
        <svg
          viewBox="0 0 420 320"
          style={{
            width: "100%",
            minWidth: 360,
            height: "auto",
            display: "block",
          }}
        >
          {[0, 1, 2, 3, 4].map((step) => {
            const value = (max / 4) * (4 - step);
            const y = 24 + step * (chartHeight / 4);
            return (
              <g key={`${title}-grid-${step}`}>
                <line
                  x1="38"
                  y1={y}
                  x2="392"
                  y2={y}
                  stroke="#E8EAED"
                  strokeWidth="1"
                />
                <text
                  x="32"
                  y={y + 4}
                  textAnchor="end"
                  fontSize="11"
                  fill="#5F6368"
                  fontFamily="Arial, sans-serif"
                >
                  {Math.round(value * 60)}
                </text>
              </g>
            );
          })}
          <line
            x1="38"
            y1={baseY}
            x2="392"
            y2={baseY}
            stroke="#BDC1C6"
            strokeWidth="1"
          />
          {chartRows.map((row, index) => {
            const height = (row.total / max) * chartHeight;
            const x = startX + index * (barWidth + gap);
            const y = baseY - height;
            const isHovered = hoveredBarLabel === row.label;
            return (
              <g key={`${title}-${row.label}`}>
                <rect
                  x={x}
                  y={y}
                  width={barWidth}
                  height={height}
                  rx="2"
                  fill={isHovered ? "#2B6DE0" : "#4285F4"}
                  onMouseEnter={() => setHoveredBarLabel(row.label)}
                  onMouseLeave={() => setHoveredBarLabel(null)}
                  style={{
                    cursor: "pointer",
                    opacity:
                      hoveredBarLabel && hoveredBarLabel !== row.label
                        ? 0.72
                        : 1,
                    filter: isHovered
                      ? "drop-shadow(0 4px 10px rgba(0,0,0,0.18))"
                      : "none",
                    transition:
                      "opacity 140ms ease, filter 140ms ease, fill 140ms ease",
                  }}
                />
                <title>{`${row.label}: ${formatHours(row.total)} hrs`}</title>
                <text
                  x={x + barWidth / 2}
                  y={Math.max(16, y - 6)}
                  textAnchor="middle"
                  fontSize="11"
                  fill={isHovered ? "#2B6DE0" : "#4285F4"}
                  fontFamily="Arial, sans-serif"
                  style={{
                    fontWeight: isHovered ? 700 : 400,
                    opacity:
                      hoveredBarLabel && hoveredBarLabel !== row.label
                        ? 0.62
                        : 1,
                    transition: "opacity 140ms ease, fill 140ms ease",
                  }}
                >
                  {Math.round(row.total * 60)}
                </text>
                <text
                  x={x + barWidth / 2}
                  y={286}
                  transform={`rotate(-42 ${x + barWidth / 2} 286)`}
                  textAnchor="end"
                  fontSize="11"
                  fill={isHovered ? "#374151" : "#5F6368"}
                  fontFamily="Arial, sans-serif"
                  style={{
                    fontWeight: isHovered ? 700 : 400,
                    opacity:
                      hoveredBarLabel && hoveredBarLabel !== row.label
                        ? 0.62
                        : 1,
                    transition: "opacity 140ms ease, fill 140ms ease",
                  }}
                >
                  {trimChartLabel(row.label, 14)}
                </text>
              </g>
            );
          })}
        </svg>
      </Box>
    </Box>
  );
};

const ChartCard: React.FC<{
  card: HouseholdChartCard;
  rows: Array<{ label: string; total: number }>;
  onMetricChange: (metric: HouseholdChartMetric) => void;
  onChartTypeChange: (chartType: HouseholdChartCard["chartType"]) => void;
  onRemove: () => void;
}> = ({ card, rows, onMetricChange, onChartTypeChange, onRemove }) => {
  return (
    <Paper
      sx={{
        p: 2,
        minHeight: 360,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        backgroundColor: "background.default",
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
      >
        <Typography variant="h6" fontWeight={800}>
          {card.title}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Select
            size="small"
            value={card.metric}
            onChange={(event) =>
              onMetricChange(event.target.value as HouseholdChartMetric)
            }
            sx={{ minWidth: 160 }}
          >
            {CHART_METRIC_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          <Select
            size="small"
            value={card.chartType}
            onChange={(event) =>
              onChartTypeChange(
                event.target.value as HouseholdChartCard["chartType"]
              )
            }
            sx={{ minWidth: 110 }}
          >
            {CHART_TYPE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          <Tooltip title="Remove chart">
            <IconButton size="small" color="error" onClick={onRemove}>
              <DeleteOutlineRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
      {card.chartType === "pie" ? (
        <GooglePieChart title={card.title} rows={rows} />
      ) : (
        <GoogleBarChart title={card.title} rows={rows} />
      )}
    </Paper>
  );
};

const HeaderFilterButton: React.FC<{
  label: string;
  activeCount: number;
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
}> = ({ label, activeCount, onClick }) => (
  <Stack direction="row" spacing={0.5} alignItems="center">
    <Typography
      variant="caption"
      fontWeight={800}
      sx={{ whiteSpace: "nowrap" }}
    >
      {label}
    </Typography>
    <Tooltip
      title={activeCount > 0 ? `${activeCount} filters applied` : "Filter"}
    >
      <IconButton size="small" onClick={onClick}>
        <FilterAltRoundedIcon
          fontSize="inherit"
          color={activeCount > 0 ? "primary" : "inherit"}
        />
      </IconButton>
    </Tooltip>
  </Stack>
);

const filterRowsForViewProfile = (
  sourceRows: HouseholdTaskRow[],
  profile?: HouseholdTableViewProfile
) => {
  if (!profile) return sourceRows;

  const normalizedSearch = profile.search.trim().toLowerCase();

  return sourceRows.filter((row) => {
    const matchesSearch =
      normalizedSearch.length === 0 ||
      [
        row.responsibility,
        row.capable,
        row.category,
        row.task,
        row.type,
        row.blockedBy,
        row.notes,
        row.frequency,
      ]
        .join(" ")
        .toLowerCase()
        .includes(normalizedSearch);

    if (!matchesSearch) return false;

    return Object.entries(profile.filters || {}).every(
      ([key, selectedValues]) => {
        if (!selectedValues || selectedValues.length === 0) return true;
        const value = String(row[key as FilterableColumn] || "").trim();
        return selectedValues.includes(value);
      }
    );
  });
};

const HouseholdTasksTool: React.FC = () => {
  const theme = useTheme();
  const saveTimerRef = useRef<number | null>(null);
  const hydratedRef = useRef(false);
  const seedRowsRef = useRef<HouseholdTaskRow[]>([]);
  const [activeTab, setActiveTab] = useState<ToolTab>("tasks");
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState<HouseholdTaskRow[]>([]);
  const [lists, setLists] = useState<HouseholdTaskDimensionLists>({
    people: [],
    categories: [],
    taskNames: [],
    types: [],
  });
  const [filters, setFilters] = useState<FiltersState>({});
  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
  const [filterColumn, setFilterColumn] = useState<FilterableColumn | null>(
    null
  );
  const [filterSearch, setFilterSearch] = useState("");
  const [manageListsOpen, setManageListsOpen] = useState(false);
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [chartProfiles, setChartProfiles] = useState<HouseholdChartProfile[]>(
    DEFAULT_HOUSEHOLD_CHART_PROFILES
  );
  const [activeChartProfileId, setActiveChartProfileId] = useState(
    DEFAULT_HOUSEHOLD_CHART_PROFILES[0].id
  );
  const [tableProfiles, setTableProfiles] = useState<
    HouseholdTableViewProfile[]
  >(DEFAULT_HOUSEHOLD_TABLE_PROFILES);
  const [activeTableProfileId, setActiveTableProfileId] = useState(
    DEFAULT_HOUSEHOLD_TABLE_PROFILES[0].id
  );
  const [activeCalculationProfileId, setActiveCalculationProfileId] = useState(
    DEFAULT_HOUSEHOLD_TABLE_PROFILES[0].id
  );
  const [chartProfileDialogOpen, setChartProfileDialogOpen] = useState(false);
  const [chartProfileDraftName, setChartProfileDraftName] = useState("");
  const [tableProfileDialogOpen, setTableProfileDialogOpen] = useState(false);
  const [tableProfileDraftName, setTableProfileDraftName] = useState("");
  const [newListValues, setNewListValues] = useState<
    Record<ManageListKey, string>
  >({
    people: "",
    categories: "",
    taskNames: "",
    types: "",
  });

  const loadSeedRows = async () => {
    const seedRows = parseHouseholdTasksCsv(HOUSEHOLD_TASKS_SEED_CSV);
    seedRowsRef.current = seedRows;
    return seedRows;
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [settings, seedRows] = await Promise.all([
          settingsService.getUserSettings(),
          loadSeedRows(),
        ]);
        const normalized = normalizeHouseholdTasksToolSettings(
          settings.household_tasks_tool_settings,
          seedRows
        );
        console.debug("[HouseholdTasksTool] Loaded settings", {
          rowCount: normalized.rows.length,
          people: normalized.lists.people.length,
        });
        setRows(normalized.rows);
        setLists(normalized.lists);
        setChartProfiles(normalized.chartProfiles);
        setActiveChartProfileId(normalized.activeChartProfileId);
        setTableProfiles(normalized.tableProfiles);
        setActiveTableProfileId(normalized.activeTableProfileId);
        setActiveCalculationProfileId(normalized.activeTableProfileId);
        const initialTableProfile =
          normalized.tableProfiles.find(
            (profile) => profile.id === normalized.activeTableProfileId
          ) || normalized.tableProfiles[0];
        setSearch(initialTableProfile?.search || "");
        setFilters(initialTableProfile?.filters || {});
      } catch (error) {
        console.error("[HouseholdTasksTool] Failed to load tool data", error);
        const fallbackRows = seedRowsRef.current;
        setRows(fallbackRows);
        setLists(deriveDimensionLists(fallbackRows));
        setChartProfiles(DEFAULT_HOUSEHOLD_CHART_PROFILES);
        setActiveChartProfileId(DEFAULT_HOUSEHOLD_CHART_PROFILES[0].id);
        setTableProfiles(DEFAULT_HOUSEHOLD_TABLE_PROFILES);
        setActiveTableProfileId(DEFAULT_HOUSEHOLD_TABLE_PROFILES[0].id);
        setActiveCalculationProfileId(DEFAULT_HOUSEHOLD_TABLE_PROFILES[0].id);
      } finally {
        hydratedRef.current = true;
        setLoading(false);
      }
    };

    void load();
  }, []);

  useEffect(() => {
    if (!hydratedRef.current || loading) return;
    if (saveTimerRef.current) {
      window.clearTimeout(saveTimerRef.current);
    }
    saveTimerRef.current = window.setTimeout(() => {
      const payload: HouseholdTasksToolSettingsV3 = {
        version: 3,
        rows,
        lists: deriveDimensionLists(rows, lists),
        chartProfiles,
        activeChartProfileId,
        tableProfiles,
        activeTableProfileId,
      };
      console.debug("[HouseholdTasksTool] Saving settings", {
        rowCount: rows.length,
        people: payload.lists.people.length,
      });
      void settingsService.saveUserSettings({
        household_tasks_tool_settings: payload as unknown,
      });
    }, 500);

    return () => {
      if (saveTimerRef.current) {
        window.clearTimeout(saveTimerRef.current);
      }
    };
  }, [
    rows,
    lists,
    chartProfiles,
    activeChartProfileId,
    tableProfiles,
    activeTableProfileId,
    loading,
  ]);

  useEffect(() => {
    if (
      tableProfiles.length > 0 &&
      !tableProfiles.some(
        (profile) => profile.id === activeCalculationProfileId
      )
    ) {
      setActiveCalculationProfileId(tableProfiles[0].id);
    }
  }, [activeCalculationProfileId, tableProfiles]);

  const derivedLists = useMemo(
    () => deriveDimensionLists(rows, lists),
    [rows, lists]
  );
  const capableOptions = useMemo(
    () =>
      sortUnique([
        ...derivedLists.people,
        ...rows.map((row) => row.capable),
        ...rows.flatMap((row) => splitPeople(row.capable)),
      ]),
    [derivedLists.people, rows]
  );

  const filteredRows = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return rows.filter((row) => {
      const matchesSearch =
        normalizedSearch.length === 0 ||
        [
          row.responsibility,
          row.capable,
          row.category,
          row.task,
          row.type,
          row.blockedBy,
          row.notes,
          row.frequency,
        ]
          .join(" ")
          .toLowerCase()
          .includes(normalizedSearch);

      if (!matchesSearch) return false;

      return Object.entries(filters).every(([key, selectedValues]) => {
        if (!selectedValues || selectedValues.length === 0) return true;
        const value = String(row[key as FilterableColumn] || "").trim();
        return selectedValues.includes(value);
      });
    });
  }, [filters, rows, search]);

  const summary = useMemo(() => {
    const totalHours = rows.reduce((sum, row) => sum + row.total, 0);
    const visibleHours = filteredRows.reduce((sum, row) => sum + row.total, 0);
    return {
      totalHours,
      visibleHours,
      totalRows: rows.length,
      visibleRows: filteredRows.length,
    };
  }, [filteredRows, rows]);
  const filteredTotals = useMemo(
    () => ({
      minutes: filteredRows.reduce((sum, row) => sum + row.minutes, 0),
      decimal: filteredRows.reduce((sum, row) => sum + row.decimal, 0),
      total: filteredRows.reduce((sum, row) => sum + row.total, 0),
    }),
    [filteredRows]
  );

  const chartData = useMemo(
    () => ({
      responsibility: sumTotalsBy(rows, (row) => row.responsibility),
      category: sumTotalsBy(rows, (row) => row.category),
      type: sumTotalsBy(rows, (row) => row.type),
      frequency: sumTotalsBy(rows, (row) => row.frequency),
      capable: sumTotalsBy(rows, (row) => row.capable),
    }),
    [rows]
  );
  const activeChartProfile = useMemo(
    () =>
      chartProfiles.find((profile) => profile.id === activeChartProfileId) ||
      chartProfiles[0],
    [activeChartProfileId, chartProfiles]
  );
  const activeTableProfile = useMemo(
    () =>
      tableProfiles.find((profile) => profile.id === activeTableProfileId) ||
      tableProfiles[0],
    [activeTableProfileId, tableProfiles]
  );
  const activeCalculationProfile = useMemo(
    () =>
      tableProfiles.find(
        (profile) => profile.id === activeCalculationProfileId
      ) || tableProfiles[0],
    [activeCalculationProfileId, tableProfiles]
  );
  const calculationRows = useMemo(
    () =>
      filterRowsForViewProfile(rows, activeCalculationProfile).filter(
        (row) =>
          row.frequency === "Daily" &&
          !EXCLUDED_CALCULATION_CATEGORIES.has(row.category)
      ),
    [activeCalculationProfile, rows]
  );
  const calculationHours = useMemo(
    () => calculationRows.reduce((sum, row) => sum + row.total, 0),
    [calculationRows]
  );
  const weddingCoverageRows = useMemo(
    () =>
      DEFAULT_LABOR_RATES.map((rate) => {
        const fundedHours = DEFAULT_WEDDING_VALUE / rate;
        const coverageMultiplier =
          calculationHours > 0 ? fundedHours / calculationHours : 0;
        const weddingsNeeded =
          DEFAULT_WEDDING_VALUE > 0
            ? (calculationHours * rate) / DEFAULT_WEDDING_VALUE
            : 0;

        return {
          rate,
          fundedHours,
          coverageMultiplier,
          weddingsNeeded,
        };
      }),
    [calculationHours]
  );

  useEffect(() => {
    if (loading) return;
    console.debug("[HouseholdTasksTool] Calculation scenario", {
      profileId: activeCalculationProfile?.id,
      profileName: activeCalculationProfile?.name,
      hours: calculationHours,
      taskCount: calculationRows.length,
    });
  }, [
    activeCalculationProfile,
    calculationHours,
    calculationRows.length,
    loading,
  ]);

  const openFilter = (
    event: React.MouseEvent<HTMLElement>,
    column: FilterableColumn
  ) => {
    setFilterAnchor(event.currentTarget);
    setFilterColumn(column);
    setFilterSearch("");
  };

  const filterOptions = useMemo(() => {
    if (!filterColumn) return [];
    const values = rows
      .map((row) => String(row[filterColumn] || "").trim())
      .filter(Boolean);
    return sortUnique(values).filter((value) =>
      value.toLowerCase().includes(filterSearch.trim().toLowerCase())
    );
  }, [filterColumn, filterSearch, rows]);

  const upsertRow = (rowId: string, patch: Partial<HouseholdTaskRow>) => {
    setRows((currentRows) =>
      currentRows.map((row) =>
        row.id === rowId ? normalizeHouseholdTaskRow({ ...row, ...patch }) : row
      )
    );
  };

  const addRow = () => {
    setRows((currentRows) => [...currentRows, createBlankHouseholdTaskRow()]);
  };

  const removeRow = (rowId: string) => {
    setRows((currentRows) => currentRows.filter((row) => row.id !== rowId));
    setEditingRowId((currentRowId) =>
      currentRowId === rowId ? null : currentRowId
    );
  };

  const resetToSeed = () => {
    const seedRows = seedRowsRef.current.map((row) =>
      normalizeHouseholdTaskRow(row)
    );
    setRows(seedRows);
    setLists(deriveDimensionLists(seedRows));
    setFilters({});
    setSearch("");
  };

  const addListValue = (key: ManageListKey) => {
    const value = newListValues[key].trim();
    if (!value) return;
    setLists((currentLists) => ({
      ...currentLists,
      [key]: sortUnique([...(currentLists[key] || []), value]),
    }));
    setNewListValues((currentValues) => ({ ...currentValues, [key]: "" }));
  };

  const removeListValue = (key: ManageListKey, value: string) => {
    setLists((currentLists) => ({
      ...currentLists,
      [key]: currentLists[key].filter((item) => item !== value),
    }));
  };

  const updateActiveProfile = (
    updater: (profile: HouseholdChartProfile) => HouseholdChartProfile
  ) => {
    setChartProfiles((currentProfiles) =>
      currentProfiles.map((profile) =>
        profile.id === activeChartProfileId ? updater(profile) : profile
      )
    );
  };

  const updateActiveTableProfile = (
    updater: (profile: HouseholdTableViewProfile) => HouseholdTableViewProfile
  ) => {
    setTableProfiles((currentProfiles) =>
      currentProfiles.map((profile) =>
        profile.id === activeTableProfileId ? updater(profile) : profile
      )
    );
  };

  const addChartCard = () => {
    const defaultMetric = CHART_METRIC_OPTIONS[0].value;
    const defaultChartType: HouseholdChartCard["chartType"] = "pie";
    const defaults = getChartDefaults(defaultMetric, defaultChartType);
    updateActiveProfile((profile) => ({
      ...profile,
      cards: [
        ...profile.cards,
        {
          id: createChartEntityId("household-chart"),
          metric: defaultMetric,
          chartType: defaultChartType,
          title: defaults.title,
          color: defaults.color,
        },
      ],
    }));
  };

  const updateChartMetric = (cardId: string, metric: HouseholdChartMetric) => {
    updateActiveProfile((profile) => ({
      ...profile,
      cards: profile.cards.map((card) =>
        card.id === cardId
          ? {
              ...card,
              metric,
              ...getChartDefaults(metric, card.chartType),
            }
          : card
      ),
    }));
  };

  const updateChartType = (
    cardId: string,
    chartType: HouseholdChartCard["chartType"]
  ) => {
    updateActiveProfile((profile) => ({
      ...profile,
      cards: profile.cards.map((card) =>
        card.id === cardId
          ? {
              ...card,
              chartType,
              ...getChartDefaults(card.metric, chartType),
            }
          : card
      ),
    }));
  };

  const removeChartCard = (cardId: string) => {
    updateActiveProfile((profile) => ({
      ...profile,
      cards: profile.cards.filter((card) => card.id !== cardId),
    }));
  };

  const saveProfileAs = () => {
    const name = chartProfileDraftName.trim();
    if (!name || !activeChartProfile) return;
    const nextProfile = normalizeChartProfile({
      id: createChartEntityId("household-profile"),
      name,
      cards: activeChartProfile.cards,
    });
    setChartProfiles((currentProfiles) => [...currentProfiles, nextProfile]);
    setActiveChartProfileId(nextProfile.id);
    setChartProfileDraftName("");
    setChartProfileDialogOpen(false);
  };

  const deleteActiveProfile = () => {
    if (!activeChartProfile || chartProfiles.length === 1) return;
    const remainingProfiles = chartProfiles.filter(
      (profile) => profile.id !== activeChartProfile.id
    );
    setChartProfiles(remainingProfiles);
    setActiveChartProfileId(remainingProfiles[0].id);
  };

  const applyTableProfile = (profileId: string) => {
    const nextProfile =
      tableProfiles.find((profile) => profile.id === profileId) ||
      tableProfiles[0];
    if (!nextProfile) return;
    setActiveTableProfileId(nextProfile.id);
    setSearch(nextProfile.search);
    setFilters(nextProfile.filters);
  };

  const saveActiveTableProfile = () => {
    if (!activeTableProfile) return;
    updateActiveTableProfile((profile) =>
      normalizeTableViewProfile({
        ...profile,
        search,
        filters,
      })
    );
  };

  const saveTableProfileAs = () => {
    const name = tableProfileDraftName.trim();
    if (!name) return;
    const nextProfile = normalizeTableViewProfile({
      id: createTableViewEntityId(),
      name,
      search,
      filters,
    });
    setTableProfiles((currentProfiles) => [...currentProfiles, nextProfile]);
    setActiveTableProfileId(nextProfile.id);
    setActiveCalculationProfileId(nextProfile.id);
    setTableProfileDraftName("");
    setTableProfileDialogOpen(false);
  };

  const deleteActiveTableProfile = () => {
    if (!activeTableProfile || tableProfiles.length === 1) return;
    const remainingProfiles = tableProfiles.filter(
      (profile) => profile.id !== activeTableProfile.id
    );
    const fallbackProfile = remainingProfiles[0];
    setTableProfiles(remainingProfiles);
    setActiveTableProfileId(fallbackProfile.id);
    setActiveCalculationProfileId(fallbackProfile.id);
    setSearch(fallbackProfile.search);
    setFilters(fallbackProfile.filters);
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight={800}>
          Loading household task tool...
        </Typography>
      </Paper>
    );
  }

  return (
    <Stack spacing={2.5}>
      <Paper
        sx={{
          p: 2.5,
          background:
            theme.palette.mode === "dark"
              ? `linear-gradient(135deg, ${alpha("#5dc4b2", 0.18)} 0%, ${alpha(
                  "#5b8def",
                  0.08
                )} 100%)`
              : `linear-gradient(135deg, ${alpha("#5dc4b2", 0.12)} 0%, ${alpha(
                  "#5b8def",
                  0.06
                )} 100%)`,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant="h4" fontWeight={900}>
              Household Tasks
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
              Editable task sheet plus the charts tab, seeded from your CSV and
              saved into your account settings.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip label={`${summary.totalRows} tasks`} color="primary" />
            <Chip label={`${formatHours(summary.totalHours)} total hrs`} />
            <Chip label={`${derivedLists.people.length} people`} />
            <Chip label={`${derivedLists.categories.length} categories`} />
          </Stack>
        </Stack>
      </Paper>

      <Stack
        direction={{ xs: "column", lg: "row" }}
        justifyContent="space-between"
        spacing={1.5}
      >
        <TextField
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search tasks, people, categories, notes..."
          sx={{ minWidth: { xs: "100%", lg: 420 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <ManageSearchRoundedIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            startIcon={<AddRoundedIcon />}
            variant="contained"
            onClick={addRow}
          >
            Add Task
          </Button>
          <Button
            startIcon={<ManageSearchRoundedIcon />}
            variant="outlined"
            onClick={() => setManageListsOpen(true)}
          >
            Manage Lists
          </Button>
          <Button
            startIcon={<FilterListOffRoundedIcon />}
            variant="outlined"
            onClick={() => {
              setFilters({});
              setSearch("");
            }}
          >
            Clear Filters
          </Button>
          <Button
            startIcon={<RefreshRoundedIcon />}
            variant="outlined"
            onClick={resetToSeed}
          >
            Reset Seed
          </Button>
        </Stack>
      </Stack>

      <Box>
        <Tabs
          value={activeTab}
          onChange={(_, value: ToolTab) => setActiveTab(value)}
          sx={folderTabsSx}
        >
          <Tab
            icon={<GridViewRoundedIcon fontSize="small" />}
            iconPosition="start"
            label="Task Sheet"
            value="tasks"
          />
          <Tab
            icon={<AutoGraphRoundedIcon fontSize="small" />}
            iconPosition="start"
            label="Charts"
            value="charts"
          />
          <Tab
            icon={<CalculateRoundedIcon fontSize="small" />}
            iconPosition="start"
            label="Calculations"
            value="calculations"
          />
        </Tabs>

        <Box sx={tabBodySx}>
          {activeTab === "tasks" ? (
            <Box sx={{ p: 2 }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1}
                justifyContent="space-between"
                sx={{ mb: 2 }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Select
                    size="small"
                    value={activeTableProfile?.id || ""}
                    onChange={(event) => applyTableProfile(event.target.value)}
                    sx={{ minWidth: 220 }}
                  >
                    {tableProfiles.map((profile) => (
                      <MenuItem key={profile.id} value={profile.id}>
                        {profile.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<SaveRoundedIcon />}
                    onClick={saveActiveTableProfile}
                  >
                    Save View
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddRoundedIcon />}
                    onClick={() => setTableProfileDialogOpen(true)}
                  >
                    Save As View
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteOutlineRoundedIcon />}
                    disabled={tableProfiles.length === 1}
                    onClick={deleteActiveTableProfile}
                  >
                    Delete View
                  </Button>
                </Stack>
                <Chip
                  label={`${
                    Object.values(filters).filter(
                      (value) => (value || []).length > 0
                    ).length
                  } column filters`}
                  variant="outlined"
                />
              </Stack>
              <TableContainer
                sx={{
                  maxHeight: "72vh",
                  "& .MuiTableCell-root": {
                    px: 0.75,
                    py: 0.5,
                  },
                  "& .MuiTableHead-root .MuiTableCell-root": {
                    py: 0.35,
                  },
                }}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {(
                        [
                          "responsibility",
                          "capable",
                          "category",
                          "task",
                          "type",
                          "blockedBy",
                          "frequency",
                        ] as FilterableColumn[]
                      ).map((column) => (
                        <TableCell key={column}>
                          <HeaderFilterButton
                            label={FILTER_LABELS[column]}
                            activeCount={filters[column]?.length || 0}
                            onClick={(event) => openFilter(event, column)}
                          />
                        </TableCell>
                      ))}
                      <TableCell>
                        <Typography variant="caption" fontWeight={800}>
                          Minutes
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" fontWeight={800}>
                          Decimal
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" fontWeight={800}>
                          Total
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" fontWeight={800}>
                          Notes
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption" fontWeight={800}>
                          Actions
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRows.map((row) => {
                      const isEditing = editingRowId === row.id;
                      const renderReadOnlyCell = (
                        value: string,
                        accentColor?: string,
                        nowrap = false
                      ) => (
                        <TableCell sx={readOnlyCellSx}>
                          <Box
                            sx={{
                              ...readOnlyContentSx,
                              color: accentColor || "text.primary",
                              fontWeight: accentColor ? 700 : 500,
                              ...(nowrap
                                ? {
                                    whiteSpace: "nowrap",
                                    wordBreak: "normal",
                                    overflowWrap: "normal",
                                  }
                                : {}),
                            }}
                          >
                            {value || "\u2014"}
                          </Box>
                        </TableCell>
                      );

                      const renderTextEditor = (
                        value: string,
                        patchKey:
                          | "responsibility"
                          | "capable"
                          | "category"
                          | "task"
                          | "type"
                          | "blockedBy"
                          | "notes",
                        placeholder: string,
                        minRows = 1
                      ) => (
                        <TableCell sx={getCellSx(theme)}>
                          <TextField
                            value={value}
                            onChange={(event) =>
                              upsertRow(row.id, {
                                [patchKey]: event.target.value,
                              } as Partial<HouseholdTaskRow>)
                            }
                            size="small"
                            placeholder={placeholder}
                            multiline
                            minRows={minRows}
                            maxRows={8}
                            fullWidth
                          />
                        </TableCell>
                      );

                      return (
                        <TableRow
                          key={row.id}
                          hover
                          onClick={() => setEditingRowId(row.id)}
                          sx={{
                            cursor: "pointer",
                            "& .MuiTableCell-root": {
                              borderBottomColor: alpha(
                                theme.palette.common.white,
                                0.06
                              ),
                            },
                            ...(isEditing
                              ? {
                                  backgroundColor: alpha(
                                    theme.palette.primary.main,
                                    0.08
                                  ),
                                }
                              : {}),
                          }}
                        >
                          {isEditing
                            ? renderTextEditor(
                                row.responsibility,
                                "responsibility",
                                "Person"
                              )
                            : renderReadOnlyCell(row.responsibility)}
                          {isEditing
                            ? renderTextEditor(
                                row.capable,
                                "capable",
                                "People who can do this"
                              )
                            : renderReadOnlyCell(row.capable)}
                          {isEditing
                            ? renderTextEditor(
                                row.category,
                                "category",
                                "Category"
                              )
                            : renderReadOnlyCell(row.category)}
                          {isEditing
                            ? renderTextEditor(row.task, "task", "Task")
                            : renderReadOnlyCell(row.task)}
                          {isEditing
                            ? renderTextEditor(row.type, "type", "Type")
                            : renderReadOnlyCell(row.type)}
                          {isEditing
                            ? renderTextEditor(
                                row.blockedBy,
                                "blockedBy",
                                "Dependency"
                              )
                            : renderReadOnlyCell(row.blockedBy)}
                          {isEditing ? (
                            <TableCell
                              sx={{ ...getCellSx(theme), minWidth: 100 }}
                            >
                              <Select
                                size="small"
                                value={row.frequency}
                                onChange={(event) =>
                                  upsertRow(row.id, {
                                    frequency: event.target
                                      .value as HouseholdTaskRow["frequency"],
                                  })
                                }
                                fullWidth
                              >
                                {HOUSEHOLD_TASK_FREQUENCIES.map((frequency) => (
                                  <MenuItem key={frequency} value={frequency}>
                                    {frequency}
                                  </MenuItem>
                                ))}
                              </Select>
                            </TableCell>
                          ) : (
                            renderReadOnlyCell(row.frequency)
                          )}
                          {isEditing ? (
                            <TableCell
                              sx={{ ...getCellSx(theme), minWidth: 80 }}
                            >
                              <TextField
                                type="number"
                                value={row.minutes}
                                onChange={(event) =>
                                  upsertRow(row.id, {
                                    minutes: Number(event.target.value) || 0,
                                  })
                                }
                                size="small"
                                inputProps={{ min: 0, step: 1 }}
                                fullWidth
                              />
                            </TableCell>
                          ) : (
                            renderReadOnlyCell(String(row.minutes))
                          )}
                          {renderReadOnlyCell(
                            formatHours(row.decimal),
                            undefined,
                            true
                          )}
                          {renderReadOnlyCell(
                            formatHours(row.total),
                            theme.palette.primary.main,
                            true
                          )}
                          {isEditing
                            ? renderTextEditor(row.notes, "notes", "Notes", 2)
                            : renderReadOnlyCell(row.notes)}
                          <TableCell
                            align="right"
                            sx={{ ...readOnlyCellSx, whiteSpace: "nowrap" }}
                            onClick={(event) => event.stopPropagation()}
                          >
                            <Tooltip
                              title={isEditing ? "Finish editing" : "Edit row"}
                            >
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setEditingRowId((currentRowId) =>
                                    currentRowId === row.id ? null : row.id
                                  )
                                }
                              >
                                <ManageSearchRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete task">
                              <IconButton
                                color="error"
                                onClick={() => removeRow(row.id)}
                                size="small"
                              >
                                <DeleteOutlineRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                  <TableFooter>
                    <TableRow
                      sx={{
                        position: "sticky",
                        bottom: 0,
                        zIndex: 3,
                        backgroundColor: alpha(
                          theme.palette.background.paper,
                          0.98
                        ),
                        backdropFilter: "blur(10px)",
                        "& .MuiTableCell-root": {
                          borderTop: "1px solid",
                          borderTopColor: alpha(
                            theme.palette.common.white,
                            0.08
                          ),
                          borderBottom: "none",
                          backgroundColor: "transparent",
                          py: 0.45,
                          px: 0.35,
                        },
                      }}
                    >
                      <TableCell colSpan={8}>
                        <Box
                          sx={{
                            ...readOnlyContentSx,
                            fontWeight: 800,
                            color: "text.primary",
                          }}
                        >
                          Totals ({filteredRows.length} rows)
                        </Box>
                      </TableCell>
                      <TableCell sx={readOnlyCellSx}>
                        <Box
                          sx={{
                            ...readOnlyContentSx,
                            fontWeight: 800,
                          }}
                        >
                          {filteredTotals.minutes}
                        </Box>
                      </TableCell>
                      <TableCell sx={readOnlyCellSx}>
                        <Box
                          sx={{
                            ...readOnlyContentSx,
                            fontWeight: 800,
                            whiteSpace: "nowrap",
                            wordBreak: "normal",
                            overflowWrap: "normal",
                          }}
                        >
                          {formatHours(filteredTotals.decimal)}
                        </Box>
                      </TableCell>
                      <TableCell sx={readOnlyCellSx}>
                        <Box
                          sx={{
                            ...readOnlyContentSx,
                            fontWeight: 900,
                            color: "primary.main",
                            whiteSpace: "nowrap",
                            wordBreak: "normal",
                            overflowWrap: "normal",
                          }}
                        >
                          {formatHours(filteredTotals.total)}
                        </Box>
                      </TableCell>
                      <TableCell sx={readOnlyCellSx}>
                        <Box
                          sx={{
                            ...readOnlyContentSx,
                            color: "text.secondary",
                            fontWeight: 700,
                          }}
                        >
                          Filtered view
                        </Box>
                      </TableCell>
                      <TableCell sx={readOnlyCellSx} />
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            </Box>
          ) : activeTab === "charts" ? (
            <Box sx={{ p: 2 }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                sx={{ mb: 2 }}
              >
                <Chip
                  label={`${formatHours(summary.totalHours)} total hours`}
                />
                <Chip
                  label={`${chartData.responsibility.length} responsibility groups`}
                />
                <Chip label={`${chartData.category.length} categories`} />
                <Chip label={`${chartData.type.length} types`} />
              </Stack>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1}
                justifyContent="space-between"
                sx={{ mb: 2 }}
              >
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Select
                    size="small"
                    value={activeChartProfile?.id || ""}
                    onChange={(event) =>
                      setActiveChartProfileId(event.target.value)
                    }
                    sx={{ minWidth: 220 }}
                  >
                    {chartProfiles.map((profile) => (
                      <MenuItem key={profile.id} value={profile.id}>
                        {profile.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<SaveRoundedIcon />}
                    onClick={() => setChartProfileDialogOpen(true)}
                  >
                    Save Profile
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteOutlineRoundedIcon />}
                    disabled={chartProfiles.length === 1}
                    onClick={deleteActiveProfile}
                  >
                    Delete Profile
                  </Button>
                </Stack>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AddRoundedIcon />}
                  onClick={addChartCard}
                >
                  Add Chart
                </Button>
              </Stack>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    lg: "repeat(2, minmax(0, 1fr))",
                  },
                  gap: 2,
                }}
              >
                {(activeChartProfile?.cards || []).map((card) => (
                  <ChartCard
                    key={card.id}
                    card={card}
                    rows={chartData[card.metric]}
                    onMetricChange={(metric) =>
                      updateChartMetric(card.id, metric)
                    }
                    onChartTypeChange={(chartType) =>
                      updateChartType(card.id, chartType)
                    }
                    onRemove={() => removeChartCard(card.id)}
                  />
                ))}
              </Box>
            </Box>
          ) : (
            <Box sx={{ p: 2 }}>
              <Stack spacing={2}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={1}
                  justifyContent="space-between"
                  alignItems={{ xs: "stretch", md: "center" }}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <Select
                      size="small"
                      value={activeCalculationProfile?.id || ""}
                      onChange={(event) =>
                        setActiveCalculationProfileId(event.target.value)
                      }
                      sx={{ minWidth: 240 }}
                    >
                      {tableProfiles.map((profile) => (
                        <MenuItem key={profile.id} value={profile.id}>
                          {profile.name}
                        </MenuItem>
                      ))}
                    </Select>
                    <Chip
                      label={`${calculationRows.length} tasks in scenario`}
                      variant="outlined"
                    />
                    <Chip
                      label="Daily only, excluding School, Rest, Business, Sleep"
                      variant="outlined"
                    />
                  </Stack>
                  <Chip
                    color="primary"
                    label={`${formatHours(calculationHours)} scheduled daily hours in ${
                      activeCalculationProfile?.name || "selected profile"
                    }`}
                  />
                </Stack>

                <Paper
                  sx={{
                    p: 2,
                    background:
                      theme.palette.mode === "dark"
                        ? `linear-gradient(135deg, ${alpha(
                            theme.palette.primary.main,
                            0.16
                          )} 0%, ${alpha("#5dc4b2", 0.14)} 100%)`
                        : `linear-gradient(135deg, ${alpha(
                            theme.palette.primary.main,
                            0.08
                          )} 0%, ${alpha("#5dc4b2", 0.08)} 100%)`,
                  }}
                >
                  <Stack spacing={1}>
                    <Typography variant="overline" sx={{ letterSpacing: 1.2 }}>
                      Monthly Framing
                    </Typography>
                    <Typography variant="h5" fontWeight={900}>
                      1 Wedding ={" "}
                      {currencyFormatter.format(DEFAULT_WEDDING_VALUE)}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {activeCalculationProfile?.name || "Selected profile"}{" "}
                      currently totals {formatHours(calculationHours)} filtered
                      daily hours of household labor after excluding School,
                      Rest, Business, and Sleep. The table below shows how many
                      hours one wedding can fund at each labor rate.
                    </Typography>
                  </Stack>
                </Paper>

                <TableContainer
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    overflow: "hidden",
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Rate</TableCell>
                        <TableCell>1 Wedding Value</TableCell>
                        <TableCell>Hours Funded</TableCell>
                        <TableCell>Scheduled Daily Hours</TableCell>
                        <TableCell>Days Covered</TableCell>
                        <TableCell>Weddings Needed</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {weddingCoverageRows.map((row) => (
                        <TableRow key={`coverage-${row.rate}`}>
                          <TableCell>
                            <Typography fontWeight={800}>
                              {currencyFormatter.format(row.rate)}/hr
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {currencyFormatter.format(DEFAULT_WEDDING_VALUE)}
                          </TableCell>
                          <TableCell>
                            {formatHours(row.fundedHours)} hrs
                          </TableCell>
                          <TableCell>
                            {formatHours(calculationHours)} hrs
                          </TableCell>
                          <TableCell>
                            {calculationHours > 0
                              ? `${row.coverageMultiplier.toFixed(2)} days`
                              : "\u2014"}
                          </TableCell>
                          <TableCell>
                            {calculationHours > 0
                              ? row.weddingsNeeded.toFixed(2)
                              : "\u2014"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Stack
                  direction={{ xs: "column", lg: "row" }}
                  spacing={2}
                  alignItems="stretch"
                >
                  {weddingCoverageRows.slice(0, 3).map((row) => (
                    <Paper
                      key={`coverage-card-${row.rate}`}
                      sx={{
                        p: 2,
                        flex: 1,
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          0.06
                        ),
                      }}
                    >
                      <Typography variant="subtitle2" color="text.secondary">
                        At {currencyFormatter.format(row.rate)}/hr
                      </Typography>
                      <Typography
                        variant="h6"
                        fontWeight={900}
                        sx={{ mt: 0.5 }}
                      >
                        {formatHours(row.fundedHours)} hours funded
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1 }}
                      >
                        That covers the current filtered{" "}
                        {formatHours(calculationHours)}-hour daily workload{" "}
                        {calculationHours > 0
                          ? `for ${row.coverageMultiplier.toFixed(2)} days`
                          : "once hours are added"}
                        .
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              </Stack>
            </Box>
          )}
        </Box>
      </Box>

      <Popover
        open={Boolean(filterAnchor && filterColumn)}
        anchorEl={filterAnchor}
        onClose={() => {
          setFilterAnchor(null);
          setFilterColumn(null);
          setFilterSearch("");
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 2, width: 320 }}>
          <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
            {filterColumn ? FILTER_LABELS[filterColumn] : "Filter"}
          </Typography>
          <TextField
            value={filterSearch}
            onChange={(event) => setFilterSearch(event.target.value)}
            placeholder="Filter options..."
            size="small"
            fullWidth
          />
          <Stack direction="row" spacing={1} sx={{ my: 1.5 }}>
            <Button
              size="small"
              onClick={() => {
                if (!filterColumn) return;
                setFilters((currentFilters) => ({
                  ...currentFilters,
                  [filterColumn]: [],
                }));
              }}
            >
              Clear
            </Button>
            <Button
              size="small"
              onClick={() => {
                if (!filterColumn) return;
                setFilters((currentFilters) => ({
                  ...currentFilters,
                  [filterColumn]: filterOptions,
                }));
              }}
            >
              Select Visible
            </Button>
          </Stack>
          <Divider sx={{ mb: 1.5 }} />
          <List dense sx={{ maxHeight: 280, overflow: "auto", py: 0 }}>
            {filterOptions.map((option) => {
              const activeValues = filterColumn
                ? filters[filterColumn] || []
                : [];
              const checked = activeValues.includes(option);
              return (
                <ListItem key={option} disablePadding>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={(event) => {
                          if (!filterColumn) return;
                          setFilters((currentFilters) => {
                            const existing = currentFilters[filterColumn] || [];
                            const nextValues = event.target.checked
                              ? sortUnique([...existing, option])
                              : existing.filter((value) => value !== option);
                            return {
                              ...currentFilters,
                              [filterColumn]: nextValues,
                            };
                          });
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2" noWrap sx={{ maxWidth: 240 }}>
                        {option}
                      </Typography>
                    }
                    sx={{ ml: 0 }}
                  />
                </ListItem>
              );
            })}
          </List>
        </Box>
      </Popover>

      <Dialog
        open={manageListsOpen}
        onClose={() => setManageListsOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Manage People, Categories, Tasks, and Types</DialogTitle>
        <DialogContent dividers>
          {(
            [
              ["people", "People"],
              ["categories", "Categories"],
              ["taskNames", "Tasks"],
              ["types", "Types"],
            ] as Array<[ManageListKey, string]>
          ).map(([key, label]) => (
            <Box key={key} sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
                {label}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                <TextField
                  value={newListValues[key]}
                  onChange={(event) =>
                    setNewListValues((currentValues) => ({
                      ...currentValues,
                      [key]: event.target.value,
                    }))
                  }
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      addListValue(key);
                    }
                  }}
                  size="small"
                  fullWidth
                  placeholder={`Add ${label.slice(0, -1)}`}
                />
                <Button
                  variant="contained"
                  startIcon={<AddRoundedIcon />}
                  onClick={() => addListValue(key)}
                >
                  Add
                </Button>
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {derivedLists[key].map((value) => (
                  <Chip
                    key={`${key}-${value}`}
                    label={value}
                    onDelete={() => removeListValue(key, value)}
                  />
                ))}
              </Stack>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManageListsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={chartProfileDialogOpen}
        onClose={() => setChartProfileDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Save Chart Dashboard Profile</DialogTitle>
        <DialogContent dividers>
          <TextField
            value={chartProfileDraftName}
            onChange={(event) => setChartProfileDraftName(event.target.value)}
            label="Profile Name"
            fullWidth
            size="small"
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChartProfileDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveRoundedIcon />}
            onClick={saveProfileAs}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={tableProfileDialogOpen}
        onClose={() => setTableProfileDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Save Task Sheet View</DialogTitle>
        <DialogContent dividers>
          <TextField
            value={tableProfileDraftName}
            onChange={(event) => setTableProfileDraftName(event.target.value)}
            label="View Name"
            fullWidth
            size="small"
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTableProfileDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveRoundedIcon />}
            onClick={saveTableProfileAs}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default HouseholdTasksTool;
